import { useNavigate } from "react-router-dom";
import type { StaffOrderItem } from "@/api/staff";
import { useOrderCommonFun } from "../../hooks/useOrderCommonFun";

interface Props {
  item: StaffOrderItem;
}

const STATUS_MAP: Record<number, string> = {
  10: "待接单",
  20: "待服务",
  30: "进行中",
  40: "待评价",
  50: "已完成",
};

type ActionLabel = string | ((is_depart: number) => string);
const ACTION_CONFIG: Record<
  number,
  Array<{
    label: ActionLabel;
    primary?: boolean;
    action?: keyof ReturnType<typeof useOrderCommonFun>;
  }>
> = {
  10: [{ label: "取消接单" }, { label: "确认接单", primary: true }],
  20: [
    {
      label: (is_depart) => (is_depart === 0 ? "查看详情" : "报备"),
      action: undefined as any,
    },
    {
      label: (is_depart) => {
        if (is_depart === 0) return "出发";
        if (is_depart === 1) return "到达";
        if (is_depart === 2) return "去打卡";
        if (is_depart >= 3) return "开始服务";
        return "";
      },
      primary: true,
      action: undefined as any,
    },
  ],
  30: [{ label: "报备" }, { label: "完成服务", primary: true }],
  40: [{ label: "查看详情" }, { label: "去评价" }],
  50: [{ label: "查看详情" }],
};

export function OrderItem({ item }: Props) {
  const navigate = useNavigate();
  const common = useOrderCommonFun(item.id);
  const actions = ACTION_CONFIG[item.status] ?? [];
  const normalizedStatus =
    item.status === 40 && Number((item as any).is_staff_comment ?? 0) === 1
      ? 50
      : item.status;
  const normalizedActions = ACTION_CONFIG[normalizedStatus] ?? actions;
  const resolveLabel = (label: ActionLabel, is_depart: number) =>
    typeof label === "function" ? label(is_depart) : label;

  return (
    <div
      onClick={common.orderDetail}
      className="bg-white mb-4 rounded-2xl overflow-hidden active:bg-gray-50 cursor-pointer"
    >
      {/* 预约时间 + 状态 */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <div className="flex items-center gap-1.5 text-[#FE7A3A] text-sm">
          <i className="ri-time-line" />
          <span className="font-semibold">预约时间：</span>
          <span>{item.book_time_desc}</span>
        </div>
        <span className="text-sm text-gray-500">{STATUS_MAP[normalizedStatus] || item.status_desc}</span>
      </div>

      {/* 订单信息 */}
      <div className="px-4 pb-4 pt-2 border-b border-gray-100">
        <p className="text-base font-bold mb-2">
          服务项目：<span>{item.item_name}</span>
        </p>
        <div className="text-xs text-gray-400 space-y-1">
          <p>
            服务时长：{item.num}
            {item.unit_name}
          </p>
          <p>下单时间：{item.add_time_desc}</p>
        </div>

        {/* 金额卡片 */}
        <div className="grid grid-cols-2 gap-3 mt-3 p-3 bg-gray-50 rounded-xl">
          <div className="flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-[#FF2525]">
              ¥{item.cost_fee}
            </span>
            <span className="text-xs text-gray-600">获得收益</span>
          </div>
          <div className="flex flex-col items-center justify-center">
            <span className="text-lg font-bold text-[#FF2525]">
              ¥{item.pay_price}
            </span>
            <span className="text-xs text-gray-600">订单金额</span>
          </div>
        </div>
      </div>

      {/* 操作按钮 */}
      <div className="px-4 pb-3 flex items-center justify-end gap-2">
        <span className="text-xs text-gray-400">
          服务距离：{item.travel_km}km
        </span>
        <div className="flex items-center">
          {normalizedActions.map((btn, i) => {
            const label = resolveLabel(btn.label, item.is_depart);
            const handler = (() => {
              // status=40 的“去评价” → 提交评价页；其它默认按 action 或详情
              if (normalizedStatus === 40 && label === "去评价") {
                return () => {
                  navigate(`/workspace/order/comment?id=${encodeURIComponent(String(item.id))}`);
                };
              }
              return btn.action ? (common[btn.action] as any) : common.orderDetail;
            })();
            return (
              <button
                key={i}
                onClick={(e) => {
                  e.stopPropagation();
                  handler();
                }}
                className={`ml-2 px-4 py-1.5 text-xs rounded-full transition-colors ${i === 0 ? "" : "first:ml-0"} ${
                  btn.primary
                    ? "bg-[#FE7A3A] text-white"
                    : "border border-gray-300 text-gray-700"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
